import BASE_URL from '../config';
import accessManager from '../Auth/Access';
class ApiService {
  constructor() {
    this.baseURL = BASE_URL;
  }

  // Helper method for making authenticated HTTP requests
  async makeRequest(url, options = {}) {
    try {
      // Get the current token
      const token = accessManager.getTokens().access_token;
      
      // Prepare headers with authentication
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };
      
      // Add authorization header if token exists
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      let response = await fetch(url, {
        headers,
        ...options,
      });

      // Handle 401 unauthorized - attempt token refresh
      if (response.status === 401 && token) {
        console.log('Received 401, attempting token refresh...');
        
        const refreshed = await accessManager.refreshTokenWithRetry();
        if (refreshed) {
          // Retry the request with the new token
          const newToken = accessManager.getTokens().access_token;
          headers.Authorization = `Bearer ${newToken}`;
          
          response = await fetch(url, {
            headers,
            ...options,
          });
        } else {
          // If refresh fails, logout and throw error
          accessManager.logout();
          throw new Error('Authentication failed - please login again');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${url}`, error);
      throw error;
    }
  }

  // Member-related endpoints
  async fetchMembers() {
    return this.makeRequest(`${this.baseURL}/names/`);
  }

  // NEW: Method for Edit.js component - returns formatted member data for selection
  async getMemberSelection() {
    try {
      const members = await this.fetchMembers();
      // Transform the data to match what Edit.js expects
      return members.map(member => ({
        id: member.id,
        name: member.name,
        member_id: member.member_id,
        phone: member.phone,
        groupName: member.group ? member.group.name : 'No Group',
        groupId: member.group ? member.group.id : null
      }));
    } catch (error) {
      console.error('Error fetching member selection:', error);
      throw error;
    }
  }

  async checkMemberByPhone(phoneNumber) {
    return this.makeRequest(`${this.baseURL}/check-member/${phoneNumber}/`);
  }

  // Transaction-related endpoints
  async fetchTransactions() {
    return this.makeRequest(`${this.baseURL}/check-transactions/`);
  }

  // NEW: Method to fetch transactions by date - matches the component's usage
  async fetchTransactionsByDate(date, branchId = null) {
    try {
      let url = `${this.baseURL}/check-transactions/?date=${date}`;
      
      // Add branch_id parameter if provided
      if (branchId !== null && branchId !== undefined) {
        url += `&branch_id=${branchId}`;
      }
      
      const response = await this.makeRequest(url);
      // Return the transactions array from the response
      return response.transactions || [];
    } catch (error) {
      console.error('Error fetching transactions by date:', error);
      throw error;
    }
  }
  
  // NEW: Real date-RANGE fetch for the Transactions page's Select Month /
  // Custom filters — one request to /check-transactions/ with start_date &
  // end_date, instead of calling fetchTransactionsByDate once per day and
  // merging client-side.
  async fetchTransactionsByDateRange(startDate, endDate, branchId = null) {
    try {
      let url = `${this.baseURL}/check-transactions/?start_date=${startDate}&end_date=${endDate}`;

      if (branchId !== null && branchId !== undefined) {
        url += `&branch_id=${branchId}`;
      }

      const response = await this.makeRequest(url);
      return response.transactions || [];
    } catch (error) {
      console.error('Error fetching transactions by date range:', error);
      throw error;
    }
  }

  // UPDATED: Modified confirmTransaction to support member_id parameter
  async confirmTransaction(transactionId, memberId = null) {
    const requestBody = {
      transaction_id: transactionId
    };
    
    // Add member_id if provided
    if (memberId) {
      requestBody.member_id = memberId;
    }

    return this.makeRequest(`${this.baseURL}/simple-confirm/`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }).then(response => {
      console.log('Simple Confirm Response:', response);
      return response;
    });    
  }

  async updateTransaction(transactionData) {
    return this.makeRequest(`${this.baseURL}/update-transaction/`, {
      method: 'POST',
      body: JSON.stringify({
        transaction_id: transactionData.mpesaRef,
        customer_name: transactionData.name,
        group: transactionData.group,
        phone_number: transactionData.phoneNo,
      }),
    });
  }

  async checkTransactionWarning(transactionId, distributions = null) {
    const requestBody = {
      transaction_id: transactionId
    };
    
    // Add distributions if provided (for Edit.js component)
    if (distributions) {
      requestBody.distributions = distributions;
    }
  
    return this.makeRequest(`${this.baseURL}/check-transaction-warning/`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  // UPDATED: Distribute transaction method to match backend endpoint
  async distributeTransaction(originalTransactionId, distributions) {
    return this.makeRequest(`${this.baseURL}/distribute-transaction/`, {
      method: 'POST',
      body: JSON.stringify({
        original_transaction_id: originalTransactionId,
        distributions: distributions
      }),
    });
  }

  // UPDATED: Method specifically for Edit.js component
  async distributePayment(distributionData) {
    try {
      console.log('Distributing payment:', distributionData);
      
      // Use the existing distributeTransaction method with proper data format
      const distributions = distributionData.allocations.map(allocation => ({
        member_id: allocation.member_id,
        amount: allocation.amount
      }));

      return await this.distributeTransaction(distributionData.transaction_id, distributions);
    } catch (error) {
      console.error('Error distributing payment:', error);
      throw error;
    }
  }

  async generateReceipt(transactionRef) {
    return this.makeRequest(`${this.baseURL}/generate-receipt/${transactionRef}/`);
  }

  // Cash collection endpoint
  async collectCash(cashData) {
    return this.makeRequest(`${this.baseURL}/collect-cash/`, {
      method: 'POST',
      body: JSON.stringify({
        member_id: cashData.memberId,
        officer_name: cashData.officerName,
        amount: parseFloat(cashData.amount),
        remarks: cashData.remarks || 'Cash Collection',
      }),
    });
  }

  // Additional utility methods

  async searchTransactions(query) {
    return this.makeRequest(`${this.baseURL}/search-transactions/?q=${encodeURIComponent(query)}`);
  }

  async getTransactionsByDateRange(startDate, endDate) {
    return this.makeRequest(`${this.baseURL}/transactions-by-date/?start=${startDate}&end=${endDate}`);
  }

  async getTransactionsByStatus(status) {
    return this.makeRequest(`${this.baseURL}/transactions-by-status/?status=${status}`);
  }

  async getMemberById(memberId) {
    return this.makeRequest(`${this.baseURL}/members/${memberId}/`);
  }

  async getTransactionStats() {
    return this.makeRequest(`${this.baseURL}/transaction-stats/`);
  }

  // Bulk operations
  async bulkConfirmTransactions(transactionIds) {
    return this.makeRequest(`${this.baseURL}/bulk-confirm-transactions/`, {
      method: 'POST',
      body: JSON.stringify({
        transaction_ids: transactionIds
      }),
    });
  }

  // UPDATED: Export transactions method to support date filtering
  async exportTransactions(filters = {}) {
    return this.makeRequest(`${this.baseURL}/export-transactions/`, {
      method: 'POST',
      body: JSON.stringify(filters),
    });
  }

  // Member management endpoints
  async createMember(memberData) {
    return this.makeRequest(`${this.baseURL}/members/`, {
      method: 'POST',
      body: JSON.stringify(memberData),
    });
  }

  async updateMember(memberId, memberData) {
    return this.makeRequest(`${this.baseURL}/members/${memberId}/`, {
      method: 'PUT',
      body: JSON.stringify(memberData),
    });
  }

  async deleteMember(memberId) {
    return this.makeRequest(`${this.baseURL}/members/${memberId}/`, {
      method: 'DELETE',
    });
  }

  // Group management endpoints
  async fetchGroups() {
    return this.makeRequest(`${this.baseURL}/groups/`);
  }

  async createGroup(groupData) {
    return this.makeRequest(`${this.baseURL}/groups/`, {
      method: 'POST',
      body: JSON.stringify(groupData),
    });
  }

  async fetchBankToClientTransactions(filters = {}) {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Default to last 60 days if no date range specified
      if (!filters.start_date && !filters.end_date) {
        const today = new Date();
        const sixtyDaysAgo = new Date();
        sixtyDaysAgo.setDate(today.getDate() - 60);
        
        params.append('start_date', sixtyDaysAgo.toISOString().split('T')[0]);
        params.append('end_date', today.toISOString().split('T')[0]);
      } else {
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
      }
      
      // Add other filters
      if (filters.classification_status) {
        params.append('classification_status', filters.classification_status);
      }
      if (filters.member_id) {
        params.append('member_id', filters.member_id);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.page) {
        params.append('page', filters.page);
      }
      if (filters.limit) {
        params.append('limit', filters.limit);
      }
      
      return this.makeRequest(`${this.baseURL}/bank-client-transactions/?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching bank to client transactions:', error);
      throw error;
    }
  }

  async getUnclassifiedTransactions(page = 1, limit = 20) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      return this.makeRequest(`${this.baseURL}/bank-client-transactions/unclassified/?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching unclassified transactions:', error);
      throw error;
    }
  }

  async getClassificationSuggestions(transactionId) {
    try {
      return this.makeRequest(`${this.baseURL}/bank-client-transactions/${transactionId}/classification_suggestions/`);
    } catch (error) {
      console.error('Error fetching classification suggestions:', error);
      throw error;
    }
  }

  async classifyTransaction(transactionId, classificationData) {
    try {
      return this.makeRequest(`${this.baseURL}/bank-client-transactions/${transactionId}/classify/`, {
        method: 'POST',
        body: JSON.stringify(classificationData),
      });
    } catch (error) {
      console.error('Error classifying transaction:', error);
      throw error;
    }
  }

  async getClassificationSummary() {
    try {
      return this.makeRequest(`${this.baseURL}/bank-client-transactions/classification_summary/`);
    } catch (error) {
      console.error('Error fetching classification summary:', error);
      throw error;
    }
  }

  // SMS balance endpoint
  async getSmsBalance() {
    return this.makeRequest(`${this.baseURL}/sms/balance/`);
  }

  // Reports endpoints
  async getDailyReport(date) {
    return this.makeRequest(`${this.baseURL}/reports/daily/?date=${date}`);
  }

  async getWeeklyReport(startDate, endDate) {
    return this.makeRequest(`${this.baseURL}/reports/weekly/?start=${startDate}&end=${endDate}`);
  }

  async getMonthlyReport(month, year) {
    return this.makeRequest(`${this.baseURL}/reports/monthly/?month=${month}&year=${year}`);
  }

  // Settings endpoints
  async getSettings() {
    return this.makeRequest(`${this.baseURL}/settings/`);
  }

  async updateSettings(settingsData) {
    return this.makeRequest(`${this.baseURL}/settings/`, {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  // Authentication helper methods (optional - for direct access)
  isAuthenticated() {
    return accessManager.isAuthenticated();
  }

  getCurrentUser() {
    return accessManager.getCurrentUser();
  }

  async logout() {
    return accessManager.logout();
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;

// Also export the class if you need to create multiple instances
export { ApiService };